import { directRevealedChildren, FORM_FIELD_PREFIX, type FormElement, type LogicRule } from '@gearbelt/global';

export type ColumnGroupRow =
  | { kind: 'field'; columnId: string; depth: number }
  | { kind: 'rule-heading'; ruleId: string; operator: LogicRule['operator']; values: string[]; depth: number };

/** Places each revealed field under a "Value is …" heading after its controlling field; saved order holds within each level. */
export function groupConditionalColumns(columnIds: string[], elements: FormElement[]): ColumnGroupRow[] {
  const elementById = new Map(elements.map((element) => [element.id, element]));
  const columnIdSet = new Set(columnIds);
  // Record columns (`title`, `assetInternalId`) share the list but carry no prefix, so they resolve to no element.
  const elementOf = (columnId: string) => (columnId.startsWith(FORM_FIELD_PREFIX) ? elementById.get(columnId.slice(FORM_FIELD_PREFIX.length)) : undefined);

  // Column ids the rule reveals, in saved order — membership comes from the shared `directRevealedChildren`.
  const revealedColumnIds = (controlling: FormElement, ruleId: string) => {
    const revealedIds = new Set(
      directRevealedChildren(elements, controlling)
        .filter((child) => child.dependsOn?.logicRuleId === ruleId)
        .map((child) => child.id),
    );
    return columnIds.filter((columnId) => {
      const element = elementOf(columnId);
      return element !== undefined && revealedIds.has(element.id);
    });
  };

  // A revealed field is only grouped when its controlling field is still listed and still reveals it (`directRevealedChildren`
  // already checks the rule is still owned); otherwise it stays top level.
  const isGroupedUnderListedRule = (columnId: string) => {
    const element = elementOf(columnId);
    const dependsOn = element?.dependsOn;
    if (!element || !dependsOn) return false;
    const controlling = elementById.get(dependsOn.fieldId);
    if (!controlling || !columnIdSet.has(`${FORM_FIELD_PREFIX}${dependsOn.fieldId}`)) return false;
    return directRevealedChildren(elements, controlling).some((child) => child.id === element.id);
  };

  const rows: ColumnGroupRow[] = [];
  const placed = new Set<string>();
  const addField = (columnId: string, depth: number) => {
    placed.add(columnId);
    rows.push({ kind: 'field', columnId, depth });
    const element = elementOf(columnId);
    if (!element) return;
    for (const rule of element.logicRules ?? []) {
      const revealed = revealedColumnIds(element, rule.id).filter((revealedColumnId) => !placed.has(revealedColumnId));
      if (revealed.length === 0) continue;
      rows.push({ kind: 'rule-heading', ruleId: rule.id, operator: rule.operator, values: rule.values, depth });
      for (const revealedColumnId of revealed) addField(revealedColumnId, depth + 1);
    }
  };

  for (const columnId of columnIds) {
    if (!isGroupedUnderListedRule(columnId)) addField(columnId, 0);
  }
  // Rules that loop back on each other leave their fields unreached; list them at the top level, as the evaluator fails open.
  for (const columnId of columnIds) {
    if (!placed.has(columnId)) addField(columnId, 0);
  }
  return rows;
}

export type RuleGroup = { ruleId: string; operator: LogicRule['operator']; values: string[]; columnIds: string[] };

/** Each controlling column's rule groups, in order, each listing only the fields its rule reveals directly. */
export function ruleGroupsByColumn(rows: ColumnGroupRow[]): Map<string, RuleGroup[]> {
  const groupsByColumn = new Map<string, RuleGroup[]>();
  const lastFieldAtDepth: string[] = [];
  const openGroupAtDepth: RuleGroup[] = [];
  for (const row of rows) {
    if (row.kind === 'field') {
      lastFieldAtDepth[row.depth] = row.columnId;
      if (row.depth > 0) openGroupAtDepth[row.depth - 1]?.columnIds.push(row.columnId);
      continue;
    }
    const controllingColumnId = lastFieldAtDepth[row.depth];
    if (!controllingColumnId) continue;
    const group: RuleGroup = { ruleId: row.ruleId, operator: row.operator, values: row.values, columnIds: [] };
    groupsByColumn.set(controllingColumnId, [...(groupsByColumn.get(controllingColumnId) ?? []), group]);
    openGroupAtDepth[row.depth] = group;
  }
  return groupsByColumn;
}

/** Puts `reorderedIds` into the places those columns already hold, in their new order; every other column stays put. */
export function reorderWithinSlots<T extends { id: string }>(columns: T[], reorderedIds: string[]): T[] {
  const columnById = new Map(columns.map((column) => [column.id, column]));
  const reordered = new Set(reorderedIds);
  let next = 0;
  return columns.map((column) => {
    if (!reordered.has(column.id)) return column;
    const replacementId = reorderedIds[next];
    next += 1;
    const replacement = columnById.get(replacementId);
    if (!replacement) {
      throw new Error(`Column ${replacementId} is not in the list being reordered`);
    }
    return replacement;
  });
}
